/**
 * Approve step, Stay or Step down, from the microdose dose step card (workbook v1.2, OPT-STEP-004 and OPT-STEP-007).
 *
 * Approve and Step down need the provider's signing gesture, checked on the server: the 6-digit signing PIN, or a
 * passkey when one is set up (the same gesture as signing a plan). Moving from HOLD to the ceiling needs a short
 * reason (OPT-HE-008, OPT-HE-010). Stay needs no gesture; while a change waits for the patient it withdraws it.
 * The reason goes to the decision log; when none is typed the server records the readiness summary.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import { api } from '../lib/api';
import { Button, InfoHint, inputClass, overlay } from './ui';
import { MicrodoseSyringe } from './MicrodoseSyringe';
import { doseLine, plain, type DoseView, type SignedLadder, type Viewer } from './MicrodoseTypes';

export type DecisionKind = 'approve' | 'stay' | 'step_down';


export function MicrodoseDecisionDialog({
  kind,
  ladder: l,
  viewer,
  firstName,
  patientId,
  onClose,
}: {
  kind: DecisionKind;
  ladder: SignedLadder;
  viewer: Viewer;
  firstName: string;
  patientId: string;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const hasPasskey = viewer.passkeys > 0;
  const [usePin, setUsePin] = useState(!hasPasskey);

  const action = kind === 'approve' ? l.options.approve : kind === 'step_down' ? l.options.stepDown : null;
  const dose: DoseView | null = action?.dose ?? null;
  const needsReason = kind === 'approve' && l.options.approve.needsReason;
  const needsGesture = kind !== 'stay';
  const reasonOk = !needsReason || reason.trim().length >= 5;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const done = () => {
    void client.invalidateQueries({ queryKey: ['microdose', patientId] });
    onClose();
  };
  const body = (gesture: unknown) => ({
    action: kind,
    reason: reason.trim() || null,
    gesture,
    expectToMg: action?.toMg ?? null,
  });
  const withPin = useMutation({
    mutationFn: () => api.post(`/microdose/ladders/${l.id}/decisions`, body(needsGesture ? { type: 'pin', pin } : null)),
    onSuccess: done,
  });
  const withPasskey = useMutation({
    mutationFn: async () => {
      const options = await api.get<Parameters<typeof startAuthentication>[0]['optionsJSON']>(`/microdose/ladders/${l.id}/passkey-options`);
      const response = await startAuthentication({ optionsJSON: options });
      return api.post(`/microdose/ladders/${l.id}/decisions`, body({ type: 'passkey', response }));
    },
    onSuccess: done,
  });
  const pending = withPin.isPending || withPasskey.isPending;
  const failure = withPin.error ?? withPasskey.error;
  const ready = reasonOk && (!needsGesture || !usePin || (viewer.pinSet && pin.length === 6));
  const submit = () => (needsGesture && !usePin ? withPasskey.mutate() : withPin.mutate());

  const title =
    kind === 'approve'
      ? l.options.approve.ceiling
        ? `Approve ceiling step to ${l.options.approve.toText} mg`
        : `Approve step to ${l.options.approve.toText} mg`
      : kind === 'step_down'
        ? `Step down to ${l.options.stepDown.toText} mg`
        : l.options.stay.withdraws
          ? `Withdraw the change, stay at ${l.current.text} mg`
          : `Stay at ${l.current.text} mg`;
  const confirmLabel = kind === 'approve' ? 'Approve and sign' : kind === 'step_down' ? 'Step down and sign' : 'Stay at this dose';

  return overlay(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center px-0 sm:items-center sm:px-4"
      style={{ background: 'color-mix(in srgb, var(--ink) 42%, transparent)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="microdose-decision-title"
      onMouseDown={(event) => event.target === event.currentTarget && !pending && onClose()}
    >
      <div className="glass-standard max-h-[92vh] w-full max-w-[480px] overflow-y-auto rounded-t-md2 p-5 shadow-[0_16px_48px_rgba(20,23,28,0.25)] sm:rounded-md2 sm:p-6" data-testid="microdose-decision">
        <div className="flex items-start gap-2">
          <h2 id="microdose-decision-title" className="font-display text-[20px] leading-tight text-navy">
            {title}
          </h2>
          <span className="mt-1">
            <InfoHint label="What happens next">
              {kind === 'stay'
                ? l.options.stay.withdraws
                  ? `${firstName}'s waiting notice is withdrawn and the dose stays at ${l.current.text} mg.`
                  : `The dose stays at ${l.current.text} mg. Nothing changes on ${firstName}'s schedule.`
                : `${firstName} gets a portal notice with the new dose; a text only says there is an update. The new dose reaches the schedule once ${firstName} taps I understand my new dose (OPT-STEP-005).`}
            </InfoHint>
          </span>
        </div>
        <p className="mt-1 text-[13.5px] text-ink-muted">
          {firstName} · {l.itemName} · now {l.current.text} mg weekly
        </p>

        {dose && (
          <div className="mt-4 rounded-lg border border-hairline bg-white px-3.5 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-display text-[22px] text-navy" style={{ fontWeight: 560 }}>
                {dose.text} mg once a week
              </p>
              <p className="text-[12.5px] text-ink-muted">{doseLine(dose)}</p>
            </div>
            <div className="mt-2">
              <MicrodoseSyringe units={dose.units} scaleUnits={dose.scaleUnits} concentration={plain(dose.concentrationMgPerMl)} compact />
            </div>
          </div>
        )}

        {kind === 'step_down' && l.options.stepDown.suggestedBy.length > 0 && (
          <p className="mt-3 text-[13px] text-navy" title={l.options.stepDown.suggestedBy.map((s) => s.rule).join(', ')}>
            <span className="font-semibold">Offered because:</span> {l.options.stepDown.suggestedBy.map((s) => s.why).join('; ')}.
          </p>
        )}
        {kind === 'stay' && l.readiness && !l.readiness.ready && (
          <p className="mt-3 text-[13px] text-ink-muted">
            Not ready: {l.readiness.checks.filter((c) => !c.pass).map((c) => c.label.toLowerCase()).join('; ')}.
          </p>
        )}

        <label className="mt-4 block">
          <span className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-navy">
            {needsReason ? 'Why the ceiling is needed (required)' : 'Reason (optional)'}
            <InfoHint label="About the reason">
              {needsReason
                ? 'Moving from HOLD to the ceiling is your choice alone and needs a short documented reason (OPT-HE-008, OPT-HE-010).'
                : 'Kept in the decision log with your name and the time. Left empty, MCOS records the readiness summary instead.'}
            </InfoHint>
          </span>
          <textarea
            className={`${inputClass} min-h-[72px] !py-2`}
            value={reason}
            maxLength={2000}
            onChange={(e) => setReason(e.target.value)}
            placeholder={needsReason ? 'For example: no clear benefit at HOLD after 8 weeks, tolerating well' : 'In your words'}
            data-testid="decision-reason"
          />
        </label>

        {needsGesture && (
          <div className="mt-3">
            {usePin ? (
              viewer.pinSet ? (
                <input
                  aria-label="Signing PIN"
                  inputMode="numeric"
                  autoComplete="off"
                  type="password"
                  maxLength={6}
                  placeholder="6-digit signing PIN"
                  className={`${inputClass} text-center ${pin ? 'tracking-[0.4em]' : ''}`}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && ready && !pending && submit()}
                  data-testid="decision-pin"
                />
              ) : (
                <p className="rounded-lg bg-paper-deep px-3 py-2.5 text-[13px] text-navy">
                  Set your 6-digit signing PIN in{' '}
                  <Link to="/credentials" className="font-semibold text-forest underline">
                    Credentials
                  </Link>{' '}
                  first. You enter it each time you approve or step down a dose.
                </p>
              )
            ) : (
              <p className="rounded-lg bg-paper-deep px-3 py-2.5 text-[13px] text-navy">You confirm with Face ID, Touch ID or your passkey.</p>
            )}
            <p className="mt-1.5 text-center text-[12.5px] text-ink-muted">
              {hasPasskey ? (
                <button type="button" className="font-semibold text-navy underline" onClick={() => setUsePin((v) => !v)}>
                  {usePin ? 'Use your passkey instead' : 'Use your signing PIN'}
                </button>
              ) : (
                'Your signing PIN, the same as for signing a plan.'
              )}
            </p>
          </div>
        )}

        {failure && (
          <p className="mt-3 text-[13px] text-alert" role="alert">
            {failure instanceof Error ? failure.message : 'Could not record the decision'}
          </p>
        )}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!ready} loading={pending}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
  );
}
